import { useState, useEffect, useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../../api/supabase'
import { useAuth } from '../../contexts/AuthContext'
import { hasAnyRole } from '../../lib/roles'
import { withBack } from '../../lib/backNav'
import { inputCls, btnSecondary, btnGreen } from '../../pages/Warehouse/types'
import type { DeliveryNote } from '../../pages/Warehouse/types'
import TransportNoteDialog from './TransportNoteDialog'
import { resolveJobPartyAddress } from '../../lib/jobPartyAddress'
import { draftFromGroupRow, type NoteDraft } from '../../lib/deliveryNoteDraft'
import type { GroupRow } from '../../lib/groupRows'

// Job -> Transport: every Release/Delivery Note on this job in one place.
//  - WAREHOUSE notes (raised from a devanning group in the Warehouse module) are read-only here;
//    "Manage in Warehouse" jumps to the Inbound row they came from.
//  - HAULAGE notes, shown as 'Created on this job' (port <-> warehouse, port -> customer, no devanning) are created/edited here and
//    confirmed collected/delivered here -- the Warehouse boards never see them.
// Replaces the never-used job_collections / job_deliveries design (see 20260930_haulage_notes.sql).

interface TransportTabProps {
  jobId: string
  jobData: { job_ref?: string } & Record<string, unknown>
  readOnly?: boolean
  // Job Group: the page of a group job. The notes shown are those of its shipment rows (delivery_notes.job_id = the row's job), a new note
  // starts by choosing the row it is for, and the row's data is carried into the draft.
  groupRows?: GroupRow[]
}

type Row = DeliveryNote & { devannings: { warehouse_id: string } | null }
type Stage = 'draft' | 'planned' | 'transit' | 'completed'

function stageOf(n: DeliveryNote): Stage {
  if (n.lifecycle_status === 'COMPLETED') return 'completed'
  if (n.transit_status === 'IN_TRANSIT') return 'transit'
  return n.lifecycle_status === 'DRAFT' ? 'draft' : 'planned'
}
const STAGE_UI: Record<Stage, { label: string; cls: string }> = {
  draft:     { label: 'DRAFT',      cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
  planned:   { label: 'PLANNED',    cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
  transit:   { label: 'IN TRANSIT', cls: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' },
  completed: { label: 'COMPLETED',  cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' },
}
const TYPE_LABEL: Record<DeliveryNote['note_type'], string> = {
  DELIVERY: 'Delivery', COLLECTION_COURIER: 'Collection (Courier)', COLLECTION_SELF: 'Collection (Self)', COLLECTION_SUPPLIER: 'Collection (Supplier)',
}
const shortDate = (d?: string | null) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' }) : '—'

export default function TransportTab({ jobId, jobData, groupRows }: TransportTabProps) {
  const groupMode = !!groupRows
  const { user, userRoles } = useAuth()
  const canConfirm = hasAnyRole(userRoles, ['ADMIN', 'OPERATIONS'])
  const jobRef = (jobData.job_ref as string | undefined) || ''
  const [subTab, setSubTab] = useState<'collections' | 'deliveries'>('collections')
  const [rows, setRows] = useState<Row[]>([])
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState<DeliveryNote | null | 'new'>(null)
  // group mode: choosing the row a new note is for, then the chosen row + the draft carried over from it
  const [picking, setPicking] = useState(false)
  const [newFor, setNewFor] = useState<{ jobId: string; jobRef: string; prefill: NoteDraft } | null>(null)
  const [pickBusy, setPickBusy] = useState(false)
  const [collectFor, setCollectFor] = useState<Row | null>(null)
  const [deliverFor, setDeliverFor] = useState<Row | null>(null)
  const [form, setForm] = useState({ at: '', vehicle: '', driver: '', receiver: '', pod: false, notes: '' })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    // job_id on delivery_notes was best-effort for older notes -- match job_ref as well.
    const q = supabase.from('delivery_notes').select('*, devannings(warehouse_id)').eq('status', 'ACTIVE').order('serial_no')
    const childIds = (groupRows ?? []).map(r => r.job_id)
    if (groupRows && childIds.length === 0) { setRows([]); setLoading(false); return }
    const { data } = await (groupRows ? q.in('job_id', childIds) : jobRef ? q.or(`job_id.eq.${jobId},job_ref.eq.${jobRef}`) : q.eq('job_id', jobId))
    setRows((data as Row[] | null) || [])
    setLoading(false)
  }, [jobId, jobRef, groupRows])
  // eslint-disable-next-line react-hooks/set-state-in-effect -- data fetch on mount / job change
  useEffect(() => { load() }, [load])

  const counts = useMemo(() => {
    const c = { draft: 0, planned: 0, transit: 0, completed: 0 }
    for (const r of rows) c[stageOf(r)]++
    return c
  }, [rows])
  const shown = subTab === 'collections' ? rows : rows.filter(r => r.fulfilment_type === 'COLLECTION_AND_DELIVERY')
  const deliveryCount = rows.filter(r => r.fulfilment_type === 'COLLECTION_AND_DELIVERY').length
  const pct = rows.length ? Math.round(counts.completed / rows.length * 100) : 0
  const backToJob = { path: `/jobs/${jobId}?tab=transport`, label: `Job ${jobRef}` }
  const whLink = (r: Row) => r.devannings?.warehouse_id
    ? withBack(`/warehouse/${r.devannings.warehouse_id}/inbound${r.source_devanning_item_id ? `?item=${r.source_devanning_item_id}&notes=1` : ''}`, backToJob)
    : null
  const firstWh = rows.find(r => r.devannings?.warehouse_id)
  const manageAll = firstWh ? withBack(`/warehouse/${firstWh.devannings!.warehouse_id}/inbound?q=${encodeURIComponent(jobRef)}&status=all`, backToJob) : null

  async function guarded(fn: () => PromiseLike<{ error: { message: string } | null }>) {
    setBusy(true); setError(null)
    const { error: e } = await fn()
    setBusy(false)
    if (e) { setError(e.message); return false }
    await load(); return true
  }
  const openCollect = (r: Row) => { setCollectFor(r); setForm({ at: new Date().toISOString().slice(0, 16), vehicle: '', driver: '', receiver: '', pod: false, notes: '' }) }
  const openDeliver = (r: Row) => { setDeliverFor(r); setForm({ at: new Date().toISOString().slice(0, 16), vehicle: '', driver: '', receiver: r.actual_receiver_name || '', pod: false, notes: '' }) }

  async function submitCollected() {
    const r = collectFor; if (!r) return
    const at = form.at ? new Date(form.at).toISOString() : new Date().toISOString()
    const cd = r.fulfilment_type === 'COLLECTION_AND_DELIVERY'
    const ok = await guarded(() => supabase.from('delivery_notes').update({
      collected_at: at, collected_by: user?.id || null,
      ...(cd ? { transit_status: 'IN_TRANSIT' } : { lifecycle_status: 'COMPLETED', completed_at: at, completed_by: user?.id || null }),
      actual_vehicle_reg: form.vehicle || null, actual_driver_name: form.driver || null, completion_notes: form.notes || null,
    }).eq('id', r.id))
    if (ok) setCollectFor(null)
  }
  async function submitDelivered() {
    const r = deliverFor; if (!r) return
    const at = form.at ? new Date(form.at).toISOString() : new Date().toISOString()
    const ok = await guarded(() => supabase.from('delivery_notes').update({
      transit_status: 'DELIVERED', lifecycle_status: 'COMPLETED', delivered_at: at, delivered_by: user?.id || null,
      completed_at: r.collected_at || at, completed_by: r.collected_by || user?.id || null,
      actual_receiver_name: form.receiver || null, pod_received: form.pod, completion_notes: form.notes || r.completion_notes || null,
    }).eq('id', r.id))
    if (ok) setDeliverFor(null)
  }
  async function voidNote(r: Row) {
    const reason = window.prompt(`Void note #${r.serial_no}? Optional reason:`)
    if (reason === null) return
    await guarded(() => supabase.from('delivery_notes').update({
      status: 'VOID', voided_at: new Date().toISOString(), voided_by: user?.id || null, void_reason: reason || null,
    }).eq('id', r.id))
  }
  async function deleteDraft(r: Row) {
    if (!window.confirm(`Delete draft note #${r.serial_no}? This can't be undone.`)) return
    await guarded(() => supabase.from('delivery_notes').delete().eq('id', r.id).eq('lifecycle_status', 'DRAFT'))
  }

  const label = 'block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1'

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-sm font-medium text-gray-800 dark:text-gray-100">
            {jobRef} · {rows.length} note{rows.length === 1 ? '' : 's'}
            {rows.length > 0 && <span className="text-gray-500 font-normal"> · {counts.completed} completed / {counts.transit} in transit / {counts.planned} planned{counts.draft ? ` / ${counts.draft} draft` : ''}</span>}
          </p>
          {rows.length > 0 && (
            <div className="mt-1.5 h-1.5 w-64 max-w-full rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden" title={`${pct}% of notes completed`}>
              <div className="h-full bg-green-500" style={{ width: `${pct}%` }} />
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          {!groupMode && manageAll && <Link to={manageAll} className="text-sm text-blue-600 hover:underline dark:text-blue-400">Manage in Warehouse →</Link>}
          <button onClick={() => (groupMode ? setPicking(true) : setModal('new'))} className={btnGreen}>+ New Collection/Delivery Note</button>
        </div>
      </div>

      <div className="flex gap-1 border-b border-gray-200 dark:border-gray-700">
        {([['collections', `Collections [${rows.length}]`], ['deliveries', `Deliveries [${deliveryCount}]`]] as const).map(([k, l]) => (
          <button key={k} onClick={() => setSubTab(k)}
            className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 ${subTab === k ? 'border-blue-600 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>{l}</button>
        ))}
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}
      {loading ? <p className="text-sm text-gray-400 py-6 text-center">Loading…</p>
        : shown.length === 0 ? <p className="text-sm text-gray-400 py-8 text-center">No {subTab === 'deliveries' ? 'deliveries' : 'collections'} for this job yet.</p>
        : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="text-xs uppercase text-gray-500 dark:text-gray-400 text-left">
                <tr>{(groupMode ? ['Serial', 'Row', 'Type', 'Status', 'Release To', 'Deliver To', 'Plts / Ctns', 'Left', 'Delivered', ''] : ['Serial', 'Type', 'Status', 'Release To', 'Deliver To', 'Plts / Ctns', 'Left', 'Delivered', '']).map(h => <th key={h} className="px-2 py-2 font-semibold whitespace-nowrap">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {shown.map(r => {
                  const st = stageOf(r); const ui = STAGE_UI[st]
                  const haul = r.note_source === 'HAULAGE'
                  const cd = r.fulfilment_type === 'COLLECTION_AND_DELIVERY'
                  const link = whLink(r)
                  return (
                    <tr key={r.id}>
                      <td className="px-2 py-2 font-mono whitespace-nowrap">#{r.serial_no}
                        <span className={`ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full ${haul ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'}`}>{haul ? 'Created on this job' : 'Generated in Warehouse'}</span>
                      </td>
                      {groupMode && <td className="px-2 py-2 font-mono text-xs whitespace-nowrap">{r.job_ref}</td>}
                      <td className="px-2 py-2 whitespace-nowrap text-xs">{cd ? 'Collection + Delivery' : TYPE_LABEL[r.note_type]}</td>
                      <td className="px-2 py-2"><span className={`text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${ui.cls}`}>{ui.label}</span></td>
                      <td className="px-2 py-2 text-xs max-w-[12rem] truncate" title={r.release_to_name}>{r.release_to_name || '—'}</td>
                      <td className="px-2 py-2 text-xs max-w-[14rem] truncate" title={r.deliver_to_addr || ''}>{cd ? (r.deliver_to_name || '—') : '—'}</td>
                      <td className="px-2 py-2 font-mono text-xs whitespace-nowrap">{r.pallets_qty} / {r.nop}</td>
                      <td className="px-2 py-2 text-xs whitespace-nowrap">{shortDate(r.collected_at || (r.lifecycle_status === 'COMPLETED' ? r.completed_at : null))}</td>
                      <td className="px-2 py-2 text-xs whitespace-nowrap">{cd ? shortDate(r.delivered_at) : '—'}</td>
                      <td className="px-2 py-2 whitespace-nowrap text-xs">
                        <div className="flex items-center gap-2">
                          {r.pdf_url && <button onClick={() => window.open(r.pdf_url!, '_blank')} className="text-blue-600 hover:underline dark:text-blue-400">PDF</button>}
                          {!haul && link && <Link to={link} className="text-blue-600 hover:underline dark:text-blue-400">Warehouse →</Link>}
                          {haul && st === 'draft' && <button onClick={() => setModal(r)} className="text-blue-600 hover:underline dark:text-blue-400">Edit</button>}
                          {haul && canConfirm && st === 'planned' && <button onClick={() => openCollect(r)} className="px-2 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium">Confirm Collected</button>}
                          {haul && canConfirm && st === 'transit' && <button onClick={() => openDeliver(r)} className="px-2 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 font-medium">Confirm Delivered</button>}
                          {haul && st === 'draft' && <button onClick={() => deleteDraft(r)} className="text-red-500 hover:underline">Delete</button>}
                          {haul && st !== 'draft' && <button onClick={() => voidNote(r)} className="text-red-500 hover:underline">Void</button>}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      {!groupMode && <p className="text-[11px] text-gray-400">Warehouse notes are read-only here — raise and manage them from Inbound, where quantities follow the real devanning groups. Notes marked "Created on this job" cover container moves that don't go through the warehouse; they use the same note editor and printout.</p>}

      {modal && <TransportNoteDialog jobId={groupMode && modal !== 'new' ? (modal.job_id ?? jobId) : jobId} jobRef={groupMode && modal !== 'new' ? modal.job_ref : jobRef} editing={modal === 'new' ? null : modal} onClose={() => setModal(null)} onSaved={load} />}
      {newFor && <TransportNoteDialog jobId={newFor.jobId} jobRef={newFor.jobRef} editing={null} prefill={newFor.prefill} onClose={() => setNewFor(null)} onSaved={load} />}
      {picking && groupRows && (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-3xl my-10">
            <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">Which shipment is this note for?</h3>
                <p className="text-xs text-gray-500">The shipment's consignee, address, cartons, weight, CBM, description and airway bill are carried into the note; you can change anything.</p>
              </div>
              <button onClick={() => setPicking(false)} className="text-gray-400 hover:text-gray-600 text-xl leading-none" aria-label="Close">×</button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto">
              {groupRows.filter(r => !r.cancelled).length === 0 && <p className="p-6 text-sm text-gray-400 text-center">This group has no shipments yet.</p>}
              <table className="min-w-full text-sm"><tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {groupRows.filter(r => !r.cancelled).map(r => (
                  <tr key={r.job_id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                    <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{r.job_ref}</td>
                    <td className="px-3 py-2 text-xs">{r.awb || <span className="text-gray-300">no airway bill</span>}</td>
                    <td className="px-3 py-2 text-xs">{r.consignee_name || <span className="text-gray-300">no consignee</span>}</td>
                    <td className="px-3 py-2 text-xs">{r.to_code || '—'}</td>
                    <td className="px-3 py-2 text-xs text-right">{r.cartons || '—'} ctns</td>
                    <td className="px-3 py-2 text-xs">{r.notes_count > 0 && <span className="px-1.5 py-0.5 rounded-full bg-teal-100 text-teal-700 text-[10px]">{r.notes_count} note{r.notes_count === 1 ? '' : 's'} already</span>}</td>
                    <td className="px-3 py-2 text-right">
                      <button disabled={pickBusy} className={btnGreen} onClick={async () => {
                        setPickBusy(true)
                        let lines: string[] = []
                        try {
                          // the address recorded for this job's consignee: the same database function the invoice and the job screen use
                          const a = r.consignee_id ? await resolveJobPartyAddress(r.job_id, 'importer') : null
                          if (a && a.source !== 'none') lines = [...(a.address_lines ?? []), a.city, a.postcode, a.country].filter((l): l is string => !!l && !!l.trim())
                        } catch { /* no address: the note opens without one, staff type it */ }
                        setPickBusy(false); setPicking(false)
                        setNewFor({ jobId: r.job_id, jobRef: r.job_ref, prefill: draftFromGroupRow(r, lines) })
                      }}>{pickBusy ? '…' : 'Create note'}</button>
                    </td>
                  </tr>
                ))}
              </tbody></table>
            </div>
          </div>
        </div>
      )}

      {(collectFor || deliverFor) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 max-w-md w-full space-y-3">
            <h3 className="font-semibold text-gray-900 dark:text-white">{collectFor ? `Confirm Collected — #${collectFor.serial_no}` : `Confirm Delivered — #${deliverFor!.serial_no}`}</h3>
            <div><label className={label}>{collectFor ? 'Actual collection date/time' : 'Actual delivered date/time'}</label>
              <input type="datetime-local" className={inputCls} value={form.at} onChange={e => setForm(f => ({ ...f, at: e.target.value }))} /></div>
            {collectFor ? (
              <div className="grid grid-cols-2 gap-3">
                <div><label className={label}>Vehicle reg</label><input className={inputCls} value={form.vehicle} onChange={e => setForm(f => ({ ...f, vehicle: e.target.value }))} /></div>
                <div><label className={label}>Driver</label><input className={inputCls} value={form.driver} onChange={e => setForm(f => ({ ...f, driver: e.target.value }))} /></div>
              </div>
            ) : (
              <>
                <div><label className={label}>Receiver name</label><input className={inputCls} value={form.receiver} onChange={e => setForm(f => ({ ...f, receiver: e.target.value }))} /></div>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.pod} onChange={e => setForm(f => ({ ...f, pod: e.target.checked }))} /> POD received</label>
              </>
            )}
            <div><label className={label}>Notes</label><textarea rows={2} className={inputCls} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} /></div>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <div className="flex justify-end gap-2">
              <button onClick={() => { setCollectFor(null); setDeliverFor(null); setError(null) }} className={btnSecondary}>Cancel</button>
              <button onClick={collectFor ? submitCollected : submitDelivered} disabled={busy} className={btnGreen}>{busy ? 'Saving…' : collectFor ? 'Confirm Collected' : 'Confirm Delivered'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
